/* Admin: Programs — create/edit/delete the foundation's programs. */
import { useState } from "react";
import AdminShell from "../../components/shell/AdminShell.jsx";
import {
  Button, Field, Input, Textarea, Card, Badge, Modal, EmptyState, Skeleton,
  ConfirmDialog, Icon, SmartImage, MediaUploaderField, RichTextEditor, showToast,
} from "../../ui/index.js";
import { usePrograms } from "../../data/hooks.js";
import { saveProgram, deleteProgram } from "../../data/mutations.js";
import { slugify } from "../../utils/format.js";

const EMPTY = { slug: "", title: "", order: 10, summary: "", bodyHtml: "", heroImage: null, published: true, isFeatured: false, pageSlug: "" };

export default function AdminProgramsPage() {
  const { programs, loading } = usePrograms();
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);

  const onDelete = async () => {
    try {
      await deleteProgram(deleting.slug, deleting.heroImage?.storagePath);
      showToast({ intent: "success", title: "Program deleted" });
    } catch (err) {
      showToast({ intent: "danger", title: "Couldn't delete", body: err.message });
    }
    setDeleting(null);
  };

  return (
    <AdminShell>
      <div className="admin-main__header">
        <div><h1 className="t-3xl">Programs</h1><p>The projects the foundation runs. Girl Power is a featured program.</p></div>
        <Button variant="primary" onClick={() => setEditing({ ...EMPTY })}><Icon.Plus size={16} />New program</Button>
      </div>

      {loading ? (
        <Skeleton h={200} />
      ) : programs.length === 0 ? (
        <Card><EmptyState icon={<Icon.Star size={40} />} title="No programs yet" body="Add your first program." action={<Button variant="primary" onClick={() => setEditing({ ...EMPTY })}>Add program</Button>} /></Card>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {programs.map((p) => (
            <Card key={p.slug}>
              <div className="row row--between" style={{ alignItems: "center", gap: 16 }}>
                <div style={{ width: 80, height: 56, borderRadius: "var(--radius)", overflow: "hidden", background: "var(--bg-warm)", flexShrink: 0 }}>
                  <SmartImage src={p.heroImage?.url} alt={p.title} label={p.title} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="row" style={{ gap: 8, alignItems: "center" }}>
                    <span className="t-lg" style={{ fontWeight: 600 }}>{p.title}</span>
                    {p.isFeatured && <Badge variant="warning" noDot>Featured</Badge>}
                    {!p.published && <Badge variant="draft">Hidden</Badge>}
                  </div>
                  <div className="t-sm muted" style={{ marginTop: 2 }}>{p.summary}</div>
                </div>
                <div className="row" style={{ gap: 4 }}>
                  <Button variant="ghost" size="sm" onClick={() => setEditing(p)} aria-label="Edit"><Icon.Pencil size={14} /></Button>
                  <Button variant="ghost" size="sm" onClick={() => setDeleting(p)} aria-label="Delete"><Icon.Trash size={14} /></Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {editing && <ProgramModal program={editing} onClose={() => setEditing(null)} />}
      <ConfirmDialog open={!!deleting} title={`Delete '${deleting?.title}'?`} body="This cannot be undone." confirmLabel="Delete" destructive onConfirm={onDelete} onClose={() => setDeleting(null)} />
    </AdminShell>
  );
}

function ProgramModal({ program, onClose }) {
  const isNew = !program.slug;
  const [form, setForm] = useState({ ...EMPTY, ...program });
  const [slugTouched, setSlugTouched] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.title.trim()) { setError("Title is required."); return; }
    const slug = form.slug || slugify(form.title);
    setSaving(true);
    try {
      await saveProgram(slug, {
        title: form.title.trim(), order: Number(form.order) || 10, summary: form.summary,
        bodyHtml: form.bodyHtml, heroImage: form.heroImage, published: !!form.published,
        isFeatured: !!form.isFeatured, pageSlug: form.pageSlug || null,
      });
      showToast({ intent: "success", title: isNew ? "Program created" : "Program updated" });
      onClose();
    } catch (err) {
      setError(err.message);
      setSaving(false);
    }
  };

  return (
    <Modal open onClose={onClose} size="lg" title={isNew ? "New program" : "Edit program"}
      footer={<><Button variant="ghost" onClick={onClose}>Cancel</Button><Button variant="primary" onClick={submit} loading={saving}>Save</Button></>}>
      <div className="stack-lg">
        <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 16 }}>
          <Field label="Title" required><Input value={form.title} onChange={(e) => { const title = e.target.value; setForm((f) => ({ ...f, title, slug: slugTouched ? f.slug : slugify(title) })); }} /></Field>
          <Field label="Order" helper="Lower first."><Input type="number" value={form.order} onChange={(e) => set("order", e.target.value)} /></Field>
        </div>
        <Field label="URL slug"><Input value={form.slug} onChange={(e) => { setSlugTouched(true); set("slug", slugify(e.target.value)); }} disabled={!isNew} helper={!isNew ? "Slug can't change after creation." : undefined} /></Field>
        <Field label="Summary" helper="One line shown in the programs grid."><Textarea value={form.summary} onChange={(e) => set("summary", e.target.value)} rows={2} /></Field>
        <Field label="Hero image"><MediaUploaderField value={form.heroImage} onChange={(v) => set("heroImage", v)} path={`programs/${form.slug || "new"}`} aspect="16/9" /></Field>
        <Field label="Body"><RichTextEditor value={form.bodyHtml} onChange={(v) => set("bodyHtml", v)} instanceKey={form.slug || "new"} /></Field>
        <Field label="Linked page slug (optional)" helper="If this program has its own full page tree (e.g. girl-power)."><Input value={form.pageSlug} onChange={(e) => set("pageSlug", e.target.value)} placeholder="girl-power" /></Field>
        <div className="row" style={{ gap: 24 }}>
          <label className="row" style={{ gap: 8, cursor: "pointer" }}><input type="checkbox" checked={!!form.published} onChange={(e) => set("published", e.target.checked)} /><span className="t-sm">Published</span></label>
          <label className="row" style={{ gap: 8, cursor: "pointer" }}><input type="checkbox" checked={!!form.isFeatured} onChange={(e) => set("isFeatured", e.target.checked)} /><span className="t-sm">Featured</span></label>
        </div>
        {error && <div className="field__error"><Icon.AlertCircle size={14} />{error}</div>}
      </div>
    </Modal>
  );
}
